import { Title } from "@circle-react-shared/Paywalls/PriceDisplay/Title";
import { Row } from "@circle-react-uikit/SkeletonLoader/components/Row";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import type { CheckoutContextProps } from "@/react/contexts/Paywalls/paywallCheckoutContext";

import { PaywallCustomization } from "../PaywallCustomization";
import { PriceContextProvider } from "../PriceSelector/PriceDescription/Price/PriceProvider";
import { PriceDisplay } from "../PriceSelector/PriceDescription/PriceDisplay";
import { PriceSelectorModal } from "./../../CheckoutDescription/PriceSelectorModal";

type CommonData = "paywall";
type PriceSelectorData =
  | "previewMutation"
  | "setSelectedPrice"
  | "isSelectedPrice"
  | "getSelectedPrice"
  | "couponPreviewMutation";

type PriceSelectorContext = Pick<
  CheckoutContextProps,
  CommonData | PriceSelectorData
> & {
  stepId?: number | string;
};

interface PriceSelectorLineProps {
  isPreviewing?: boolean;
  useHooks: () => PriceSelectorContext;
}

export const PriceSelectorLine = Object.assign(
  ({ useHooks, isPreviewing }: PriceSelectorLineProps) => {
    const { paywall, couponPreviewMutation, getSelectedPrice, stepId } =
      useHooks();

    // price selection
    const priceSelected = getSelectedPrice();
    const pricesLength = paywall.prices.length;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const onClose = () => {
      if (!isPreviewing) {
        setIsModalOpen(false);
      }
    };
    const onOpen = () => {
      if (!isPreviewing) {
        setIsModalOpen(true);
      }
    };

    // An offered paywall can have no selectable price, such as an Add-on with missing prices.
    if (!priceSelected) return null;

    return (
      <>
        {stepId !== undefined && (
          <PriceSelectorModal
            stepId={stepId}
            onClose={onClose}
            isOpen={isModalOpen}
          />
        )}
        <div className="flex flex-row justify-between">
          <PriceContextProvider Title={Title.OfferPage}>
            <PaywallCustomization.PriceCallout price={priceSelected}>
              <PaywallCustomization.ReducedPrice
                price={priceSelected}
                couponPreviewMutation={couponPreviewMutation}
              >
                <PriceDisplay price={priceSelected} paywall={paywall} />
              </PaywallCustomization.ReducedPrice>
            </PaywallCustomization.PriceCallout>
          </PriceContextProvider>
          {pricesLength > 0 && (
            <Button
              variant="link"
              small
              className="!p-0 text-left"
              type="button"
              onClick={onOpen}
              data-testid="price-selector-line"
            >
              <Typography.LabelSm
                color={classNames("underline text-secondary")}
              >
                <span className="cursor-pointer">
                  {pricesLength === 1 && t("view_order_details")}
                  {pricesLength > 1 &&
                    t(
                      "paywall_checkout.offer_page.price_selection.price_options",
                      { count: pricesLength },
                    )}
                </span>
              </Typography.LabelSm>
            </Button>
          )}
        </div>
      </>
    );
  },
  {
    Skeleton: () => (
      <div className="flex flex-row justify-between">
        <Row half xl />
        <Row half xl />
      </div>
    ),
  },
);
